/**
 * Modul Lidé — sdílené konstanty a výpočty.
 *
 * Co se ukazuje na víc místech (seznam, karta, třetí sloupec, mobilní modal
 * „K dořešení"), se počítá tady — jednou. Jinak by se podoby rozešly u první
 * změny (norma §1f: obsah panelu a mobilního modalu z jedné funkce).
 */

export const SECTION_COLOR = '#B47AEA';
export const SECTION_BG = 'rgba(180,122,234,0.12)';

// Barvu nesou jen dva stavy (norma §3): v pořádku × problém.
export const OK = '#059669';
export const BAD = '#EF4444';

export const BRZY_DNI = 30;
const DEN = 86400000;

export const STAV_LABEL = {
    aktivni: 'Aktivní',
    neaktivni: 'Neaktivní',
    zkusebni: 'Zkušební',
    novy: 'Nový',
    vyplneno: 'Vyplněno',
    priprava_smlouvy: 'Příprava smlouvy',
    podpis_smlouvy: 'Podpis smlouvy',
    zaskoleni: 'V zaškolení',
};

/** Stavy, ve kterých člověk ještě nastupuje (onboarding běží). */
export const STAVY_NASTUPU = ['novy', 'vyplneno', 'priprava_smlouvy', 'podpis_smlouvy', 'zaskoleni'];

export const ONBOARDING_KROKY = [
    { key: 'dotaznik', label: 'Dotazník' },
    { key: 'smlouva', label: 'Smlouva' },
    { key: 'podpis', label: 'Podpis' },
    { key: 'certifikace', label: 'Certifikace' },
    { key: 'smernice', label: 'Směrnice' },
    { key: 'dokumenty', label: 'Dokumenty' },
];

export const celeJmeno = (z) => (z ? (z.cele_jmeno || `${z.jmeno ?? ''} ${z.prijmeni ?? ''}`.trim()) : '');
export const prijmeniJmeno = (z) => (z ? `${z.prijmeni ?? ''} ${z.jmeno ?? ''}`.trim() : '');

/** 1. 7. 2024 */
export function datum(v) {
    if (!v) return null;
    const d = new Date(String(v).split('T')[0] + 'T00:00:00');
    if (isNaN(d)) return null;
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

/** 02/27 — do úzkého sloupce seznamu */
export function mesicRok(v) {
    if (!v) return null;
    const d = new Date(String(v).split('T')[0] + 'T00:00:00');
    if (isNaN(d)) return null;
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
}

/** Certifikáty, které pozice (kariéry) zaměstnance vyžadují — kódy typů. */
export function pozadovaneCertifikaty(z) {
    return [...new Set((z?.kariery || []).flatMap(k => k.pozadovane_certifikace || []))];
}

/**
 * Kolik z platnosti certifikátu už uteklo — 0–1 pro pruh v panelu, jinak null.
 *
 * Bez data vydání se pruh nekreslí: natáhnout ho od nuly by tvrdilo, že
 * certifikát platí od začátku času, a u ročního školení by vypadal stejně
 * spolehlivě jako u pětiletého.
 */
function podilPlatnosti(cert, dnes) {
    if (!cert?.datum_certifikace || !cert?.platnost_do) return null;
    const od = new Date(String(cert.datum_certifikace).split('T')[0] + 'T00:00:00');
    const konec = new Date(String(cert.platnost_do).split('T')[0] + 'T00:00:00');
    const celkem = konec - od;
    if (!(celkem > 0)) return null;
    return Math.min(1, Math.max(0, (dnes - od) / celkem));
}

/**
 * Stav jednoho certifikátu zaměstnance.
 * `{ stav: 'nevyzaduje'|'chybi'|'expirovano'|'brzy'|'ok', cert, dni, podil }`
 * `brzy` se nekreslí třetí barvou, jen tučně s počtem dní (norma §3).
 */
export function stavCertifikatu(z, kod, dnes = new Date()) {
    const cert = (z?.certifikace || []).find(c => c.typ === kod) || null;
    const vyzaduje = pozadovaneCertifikaty(z).includes(kod);
    if (!cert) return { stav: vyzaduje ? 'chybi' : 'nevyzaduje', cert: null, dni: null, podil: null };
    const dni = Math.ceil((new Date(cert.platnost_do) - dnes) / DEN);
    const podil = podilPlatnosti(cert, dnes);
    if (dni < 0) return { stav: vyzaduje ? 'expirovano' : 'nevyzaduje', cert, dni, podil };
    if (dni <= BRZY_DNI) return { stav: 'brzy', cert, dni, podil };
    return { stav: 'ok', cert, dni, podil };
}

/**
 * „za 11 dní", „za 6 měs." — zbývající platnost do řádku certifikátu.
 *
 * Datum („do 7. 4. 2027") neřekne, jestli to hoří: obsluha by musela od oka
 * odečítat od dneška. Přesné datum zůstává v `title` a v Kvalifikaci, kde se
 * certifikát edituje.
 */
export function zbyva(dni) {
    if (dni == null || dni < 0) return null;
    if (dni === 0) return 'dnes';
    if (dni < 45) return `za ${dni} ${dni === 1 ? 'den' : dni < 5 ? 'dny' : 'dní'}`;
    const mesicu = Math.round(dni / 30.44);
    if (mesicu < 24) return `za ${mesicu} měs.`;
    const roku = Math.floor(mesicu / 12);
    return `za ${roku} ${roku < 5 ? 'roky' : 'let'}`;
}

/** Má zaměstnanec problém s některým požadovaným certifikátem? */
export function chybiCertifikat(z) {
    return pozadovaneCertifikaty(z).some(kod => ['chybi', 'expirovano'].includes(stavCertifikatu(z, kod).stav));
}

export function expirujeBrzy(z) {
    return (z?.certifikace || []).some(c => stavCertifikatu(z, c.typ).stav === 'brzy');
}

export function vNastupu(z) {
    return STAVY_NASTUPU.includes(z?.stav);
}

/** Kolik kroků onboardingu je hotových: `{ hotovo, celkem }`, nebo null. */
export function onboarding(checklist) {
    if (!checklist) return null;
    return { hotovo: ONBOARDING_KROKY.filter(k => checklist[k.key]).length, celkem: ONBOARDING_KROKY.length };
}

/**
 * K dořešení u zaměstnance — jeden seznam pro třetí sloupec i mobilní modal.
 *
 * @param {Object} z - zaměstnanec (seznamový tvar: certifikace, kariery, stav)
 * @param {Object} kontext - `{ certTypy, nepodepsane, checklist }`
 *        `nepodepsane` = dokumenty zaměstnance čekající na jeho podpis
 * @returns {Array<{id, problem: boolean, titulek, popis, akce?}>}
 *        `problem` = červeně; bez něj neutrální upozornění
 */
export function kDoreseniZamestnance(z, { certTypy = [], nepodepsane = [], checklist = null } = {}) {
    if (!z) return [];
    const nazevCert = (kod) => certTypy.find(t => t.kod === kod)?.nazev || kod;
    const polozky = [];

    for (const kod of pozadovaneCertifikaty(z)) {
        const s = stavCertifikatu(z, kod);
        if (s.stav === 'chybi') {
            polozky.push({ id: `cert-${kod}`, problem: true, titulek: `Chybí ${nazevCert(kod)}`, popis: `pozice ho vyžaduje`, druh: 'skoleni' });
        } else if (s.stav === 'expirovano') {
            polozky.push({ id: `cert-${kod}`, problem: true, titulek: `${nazevCert(kod)} expiroval`, popis: `platnost skončila ${datum(s.cert.platnost_do)}`, druh: 'skoleni' });
        }
    }
    for (const c of z.certifikace || []) {
        const s = stavCertifikatu(z, c.typ);
        if (s.stav === 'brzy') {
            polozky.push({ id: `brzy-${c.typ}`, problem: false, titulek: `${nazevCert(c.typ)} expiruje za ${s.dni} ${s.dni === 1 ? 'den' : s.dni < 5 ? 'dny' : 'dní'}`, popis: `platí do ${datum(c.platnost_do)}`, druh: 'skoleni' });
        }
    }

    for (const d of nepodepsane) {
        const dni = d.uploaded_at ? Math.floor((Date.now() - new Date(d.uploaded_at)) / DEN) : null;
        polozky.push({
            id: `dok-${d.id}`, problem: true, titulek: `${d.nazev} — nepodepsáno`,
            popis: dni != null ? `čeká ${dni} ${dni === 1 ? 'den' : dni < 5 ? 'dny' : 'dní'}` : 'čeká na podpis zaměstnance',
            druh: 'podpis', dokument: d,
        });
    }

    const ob = onboarding(checklist);
    if (ob && vNastupu(z) && ob.hotovo < ob.celkem) {
        const chybi = ONBOARDING_KROKY.filter(k => !checklist[k.key]).map(k => k.label.toLowerCase()).join(', ');
        polozky.push({ id: 'onboarding', problem: false, titulek: `Nástup ${ob.hotovo}/${ob.celkem}`, popis: `chybí: ${chybi}`, druh: 'onboarding' });
    }

    return polozky;
}
